/**
 * /docs/first-app — opinionated walkthrough. Written for the in-browser
 * path (the default); a callout per step flags where Claude Code differs.
 */

import { IconBolt } from '../../components/Icons.js';
import { ButtonLink, PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { Callout, CodeBlock, DocSection } from './_shared.js';

export function DocsFirstApp() {
  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs · First app"
        title="Build your first Kin app"
        description="By the end of this page you'll have a real app, a real URL, and enough mechanics to keep going on your own."
      />

      <DocsTabs />

      <DocSection title="Before you start">
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            You&apos;ve picked a path on{' '}
            <a className="underline" href="/docs/getting-started">
              Getting started
            </a>
            . In the browser is fastest &mdash; the rest of this page assumes that.
          </li>
          <li>
            You have something specific in mind. Vague prompts produce vague apps. Specific can mean
            two sentences.
          </li>
        </ul>
        <div className="mt-3">
          <ButtonLink to="/apps/new" variant="primary" size="sm">
            <IconBolt size={13} />
            Create your first app
          </ButtonLink>
        </div>
      </DocSection>

      <DocSection id="step-1" title="Step 1 — Create the app shell">
        <p>
          On the <strong>Apps</strong> page, click <strong>Start building</strong> &rarr;{' '}
          <em>Create app</em>. Pick a name and a slug, check the database box if it needs to
          remember anything between visits, and submit. You land on the preview URL with the chat
          bubble open in the lower-right corner.
        </p>
        <Callout tone="tip" title="If you're using Claude Code instead">
          <p>
            Skip the portal &mdash; just type something like{' '}
            <em>
              &ldquo;Make me a Kin app to track who&apos;s bringing what to team lunch.&rdquo;
            </em>{' '}
            The word <strong>Kin</strong> activates the skill; Claude Code creates the app, picks a
            slug, attaches a database, and deploys preview in one go.
          </p>
        </Callout>
      </DocSection>

      <DocSection id="step-2" title="Step 2 — Say what you want">
        <p>Tell the bubble what the app should do. Plain English is fine.</p>
        <CodeBlock
          body={`Track who's bringing what to team lunch each Friday.
Anyone at Mytra can add an entry. Show a table grouped by date.
Let people delete their own entry but not edit it after submitting.`}
          caption="example prompt"
        />
        <p>
          The agent will ask a clarifying question or two if anything is genuinely ambiguous &mdash;
          shape, naming, identity. Answer in plain English; nothing to translate.
        </p>
      </DocSection>

      <DocSection id="step-3" title="Step 3 — Watch it build">
        <p>
          Creating the app, attaching the database, and deploying preview takes 30 to 90 seconds.
          You&apos;ll see the agent edit files, run a migration, and deploy &mdash; each step shows
          as a chip in the chat.
        </p>
        <p>
          When the agent finishes, the bubble prompts: <em>&ldquo;Reload to see changes.&rdquo;</em>{' '}
          Click it. Your preview now serves what you asked for.
        </p>
      </DocSection>

      <DocSection id="step-4" title="Step 4 — Try it">
        <p>Use the app like a real user. Add an entry. Reload. Check:</p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>Does the form match what you asked for?</li>
          <li>Does the data stick? Add an entry, reload, look for it.</li>
          <li>Does it look reasonable on your phone?</li>
          <li>Anything missing?</li>
        </ul>
        <Callout tone="tip" title="It's just preview">
          <p>
            The <span className="font-mono text-xs">preview-</span> URL is where iteration happens.
            Nobody finds it unless you share the link. Promote to{' '}
            <span className="font-mono text-xs">https://{`{slug}`}.mkin.app</span> only when
            you&apos;re happy &mdash; Step 6.
          </p>
        </Callout>
      </DocSection>

      <DocSection id="step-5" title="Step 5 — Iterate">
        <p>
          Tell the bubble what to change. The agent edits the files and redeploys preview at the
          same URL.
        </p>
        <CodeBlock
          body={`Add a column for dietary notes (vegetarian, gluten-free, etc.), and
let me sort the table by date.`}
          caption="follow-up prompt"
        />
        <Callout title="Point at things on the page">
          <p>
            Click the crosshair icon next to the chat input, then click any element on the preview.
            The agent gets a reference to that element in your next prompt &mdash; useful when
            something is awkward to describe in words (&ldquo;this button,&rdquo; &ldquo;the right
            column&rdquo;).
          </p>
        </Callout>
        <Callout tone="tip" title="Don't restart from scratch">
          <p>
            If something&apos;s wrong, tell the agent what&apos;s wrong. It can fix in place.
            &ldquo;Start over&rdquo; loses your data and your iteration history.
          </p>
        </Callout>
      </DocSection>

      <DocSection id="step-6" title="Step 6 — Publish and share">
        <p>When preview is right:</p>
        <CodeBlock body="Looks good — promote it to live." caption="prompt" />
        <p>
          The agent runs a health check and copies the preview bundle to production. You get the
          live URL:
        </p>
        <CodeBlock body="https://lunch-signups.mkin.app" caption="live URL" />
        <p>
          Share that link with your team. It&apos;s visible to anyone at Mytra by default; flip it
          to private under the app&apos;s <strong>Access</strong> tab.
        </p>
        <p>
          If something breaks after publishing, ask the agent to{' '}
          <em>&ldquo;restore the previous version.&rdquo;</em> Or open the <strong>Versions</strong>{' '}
          tab and click <em>Restore</em> &mdash; instant, no redeploy.
        </p>
      </DocSection>

      <DocSection title="What's next">
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <a className="underline" href="/docs/prompting">
              How to prompt well
            </a>{' '}
            &mdash; better results from the same agent.
          </li>
          <li>
            <a className="underline" href="/docs/examples">
              Examples
            </a>{' '}
            &mdash; concrete apps with the prompts that built them.
          </li>
          <li>
            <a className="underline" href="/docs/faq">
              FAQ
            </a>{' '}
            &mdash; secrets, scheduling, rollbacks.
          </li>
        </ul>
      </DocSection>
    </div>
  );
}
